import {
  ACCESS_LEVEL_DESCRIPTIONS,
  ACCESS_LEVEL_LABELS,
  ACCESS_LEVELS,
  type AccessLevel,
  type AccessSettings,
  APP_MODULES,
  DATA_SCOPES,
  type DataScope,
  DEFAULT_DATA_SCOPE,
  levelAtLeast,
  type ModuleAccess,
  type ModuleDefinition,
} from '@k-management/shared';

const MODULES: readonly ModuleDefinition[] = APP_MODULES;
const INHERIT = 'inherit';

type Modules = Record<string, ModuleAccess | undefined>;
type Features = Record<string, boolean | undefined>;

/**
 * Levels, data scopes and features, module by module (docs/adr/ADR-008).
 *
 * - `profile`: the settings of a profile.
 * - `exceptions` with `base`: the exceptions of a user to their profile; every
 *   choice can be left «come il ruolo».
 * - `exceptions` without `base`: a user without profile, whose settings are
 *   the only ones; a feature equal to its default is not stored, so it follows
 *   the profile if one is assigned later.
 */
export function AccessEditor({
  mode,
  value,
  base = null,
  onChange,
}: {
  mode: 'profile' | 'exceptions';
  value: AccessSettings;
  base?: AccessSettings | null;
  onChange: (value: AccessSettings) => void;
}) {
  const modules = value.modules as Modules;
  const features = value.featureOverrides as Features;
  const baseModules = (base?.modules ?? {}) as Modules;
  const baseFeatures = (base?.featureOverrides ?? {}) as Features;
  const inheriting = mode === 'exceptions' && base !== null;

  const setModule = (key: string, access: ModuleAccess | undefined) => {
    const next = { ...modules };
    if (access) next[key] = access;
    else delete next[key];
    onChange({ ...value, modules: next as AccessSettings['modules'] });
  };
  const setFeature = (key: string, granted: boolean | undefined) => {
    const next = { ...features };
    if (granted === undefined) delete next[key];
    else next[key] = granted;
    onChange({ ...value, featureOverrides: next as AccessSettings['featureOverrides'] });
  };

  return (
    <div className="stack">
      {MODULES.map((module) => {
        const own = modules[module.key];
        const inherited = baseModules[module.key];
        const level: AccessLevel = own?.level ?? inherited?.level ?? 'none';
        const scope: DataScope = own?.scope ?? inherited?.scope ?? DEFAULT_DATA_SCOPE;

        return (
          <div key={module.key} className="module-access">
            <div className="module-access-header">
              <div>
                <strong>{module.label}</strong>
                {inheriting && (
                  <small className="muted"> · risultato: {describe(module, level, scope)}</small>
                )}
              </div>
              {inheriting ? (
                <select
                  aria-label={`Livello per ${module.label}`}
                  value={own ? own.level : INHERIT}
                  onChange={(e) =>
                    setModule(
                      module.key,
                      e.target.value === INHERIT
                        ? undefined
                        : {
                            level: e.target.value as AccessLevel,
                            ...(own?.scope && { scope: own.scope }),
                          },
                    )
                  }
                >
                  <option value={INHERIT}>
                    Come il profilo ({ACCESS_LEVEL_LABELS[inherited?.level ?? 'none']})
                  </option>
                  {ACCESS_LEVELS.map((option) => (
                    <option key={option} value={option}>
                      {ACCESS_LEVEL_LABELS[option]}
                    </option>
                  ))}
                </select>
              ) : (
                <div
                  className="segmented"
                  role="radiogroup"
                  aria-label={`Livello per ${module.label}`}
                >
                  {ACCESS_LEVELS.map((option) => (
                    <label
                      key={option}
                      className={option === level ? 'selected' : undefined}
                      title={ACCESS_LEVEL_DESCRIPTIONS[option]}
                    >
                      <input
                        type="radio"
                        name={`level-${mode}-${module.key}`}
                        checked={option === level}
                        onChange={() =>
                          setModule(
                            module.key,
                            option === 'none'
                              ? undefined
                              : { level: option, ...(own?.scope && { scope: own.scope }) },
                          )
                        }
                      />
                      {ACCESS_LEVEL_LABELS[option]}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {module.dataScope && level !== 'none' && (
              <div className="module-access-row">
                <span className="muted">Quali dati</span>
                {inheriting ? (
                  <select
                    aria-label={`Visibilità dei dati in ${module.label}`}
                    value={own?.scope ?? INHERIT}
                    onChange={(e) => {
                      const next =
                        e.target.value === INHERIT ? undefined : (e.target.value as DataScope);
                      if (own)
                        setModule(module.key, { level: own.level, ...(next && { scope: next }) });
                      else if (next) setModule(module.key, { level, scope: next });
                    }}
                  >
                    <option value={INHERIT}>
                      Come il profilo ({module.dataScope[inherited?.scope ?? DEFAULT_DATA_SCOPE]})
                    </option>
                    {DATA_SCOPES.map((option) => (
                      <option key={option} value={option}>
                        {module.dataScope![option]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div
                    className="segmented"
                    role="radiogroup"
                    aria-label={`Visibilità dei dati in ${module.label}`}
                  >
                    {DATA_SCOPES.map((option) => (
                      <label key={option} className={option === scope ? 'selected' : undefined}>
                        <input
                          type="radio"
                          name={`scope-${mode}-${module.key}`}
                          checked={option === scope}
                          onChange={() => setModule(module.key, { level, scope: option })}
                        />
                        {module.dataScope![option]}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {level !== 'none' && module.features.length > 0 && (
              <ul className="feature-list">
                {module.features.map((feature) => {
                  const available = levelAtLeast(level, feature.level);
                  const inheritedGrant = baseFeatures[feature.key] ?? feature.defaultGranted;
                  const granted = features[feature.key] ?? inheritedGrant;
                  const requirement = `Richiede «${ACCESS_LEVEL_LABELS[feature.level]}»`;
                  return (
                    <li key={feature.key}>
                      {inheriting ? (
                        <label className={available ? 'feature-choice' : 'feature-choice muted'}>
                          <span>
                            {feature.label}
                            <small>{available ? feature.description : requirement}</small>
                          </span>
                          <select
                            disabled={!available}
                            value={
                              features[feature.key] === undefined
                                ? INHERIT
                                : features[feature.key]
                                  ? 'yes'
                                  : 'no'
                            }
                            onChange={(e) =>
                              setFeature(
                                feature.key,
                                e.target.value === INHERIT ? undefined : e.target.value === 'yes',
                              )
                            }
                          >
                            <option value={INHERIT}>
                              Come il profilo ({inheritedGrant ? 'sì' : 'no'})
                            </option>
                            <option value="yes">Sì</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                      ) : (
                        <label className={available ? 'checkbox' : 'checkbox muted'}>
                          <input
                            type="checkbox"
                            disabled={!available}
                            checked={available && granted}
                            onChange={(e) =>
                              setFeature(
                                feature.key,
                                e.target.checked === feature.defaultGranted
                                  ? undefined
                                  : e.target.checked,
                              )
                            }
                          />
                          <span>
                            {feature.label}
                            {!feature.defaultGranted && <span className="tag">riservata</span>}
                            <small>{available ? feature.description : requirement}</small>
                          </span>
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function describe(module: ModuleDefinition, level: AccessLevel, scope: DataScope): string {
  const text = ACCESS_LEVEL_LABELS[level];
  return module.dataScope && level !== 'none'
    ? `${text}, ${module.dataScope[scope].toLowerCase()}`
    : text;
}
